// cn — class-name merger for Tailwind v4 with the `tw:` prefix.
//
// Purpose: let DS components (LeadButton, LeadInput, LeadSelect…) safely accept
// consumer overrides via `class="…"` without needing `!` on every conflicting
// utility. Without merging, `tw:h-7` from the DS and `tw:h-6` from a call site
// live in the same @layer, and source order in the compiled CSS decides — which
// usually means the DS wins because it's imported first.
//
// With `twMerge`, `cn('tw:h-7 tw:h-6') === 'tw:h-6'` at RUNTIME. Consumer wins,
// as intended.
//
// This matches the DS's own approach (see `lib/cn` in the design system source).
//
// NOTE: `!`-marked classes (e.g. `tw:text-white!`) still win via CSS specificity
// and are the right escape hatch when we need to beat lithium/Tabler element
// resets that live outside our Tailwind layer.
import { extendTailwindMerge } from 'tailwind-merge';

export const cn = extendTailwindMerge({
  // The project imports Tailwind with `prefix(tw)`, so every utility looks like
  // `tw:flex`, `tw:hover:bg-white`, etc. tailwind-merge needs to know so it can
  // parse conflicts across variants correctly.
  prefix: 'tw',
  extend: {
    // Theme tokens defined in `src/assets/tailwind.css` — tell tailwind-merge
    // they belong to standard utility groups so custom values conflict with
    // Tailwind's built-ins (e.g. `h-control` vs `h-6`).
    theme: {
      // spacing feeds height/width/padding/margin/gap groups
      spacing: ['gutter', 'band', 'control'],
      // radius feeds `rounded-*`
      radius: ['sm', 'md'],
      // shadow feeds `shadow-*`
      shadow: ['xs', 'card', 'raised', 'selected'],
    },
    classGroups: {
      // custom `text-<size>` values (text-10, text-11, text-12, text-13, text-14…)
      'font-size': [{ text: ['10', '11', '12', '13', '14', '16', '18', '20', '30'] }],
      // custom colours registered as `--color-*`
      'text-color': [
        {
          text: [
            'body',
            'muted',
            'primary',
            'primary-lt',
            'primary-darken',
            'danger',
            'surface',
            'blue-500',
            'success-700',
            'red-600',
            'orange-600',
            'pill-fg',
          ],
        },
      ],
      'bg-color': [
        {
          bg: [
            'body',
            'body-bg',
            'muted',
            'primary',
            'primary-lt',
            'primary-darken',
            'danger',
            'surface',
            'light',
            'blue-50',
            'success-50',
            'red-50',
            'orange-50',
          ],
        },
      ],
      'border-color': [
        {
          border: [
            'border',
            'border-secondary',
            'muted',
            'primary',
            'danger',
            'blue-200',
            'success-200',
            'red-200',
            'orange-200',
            'pill-border',
          ],
        },
      ],
    },
  },
});
